// =============================================================================
// Art direction: what the picture should be, before anyone asks how to shoot it.
//
// This exists because of one line that used to be appended to EVERY image
// prompt in the product: "clean and simple product photography, one clear
// subject, plain uncluttered background". It was written for a provision shop
// selling a bag of rice, and it was applied to everything — a restaurant's
// food, a tracking company's vehicles, a salon's work. Whatever concept the
// caption model chose upstream, the picture came out as the same object on the
// same empty background, which is exactly why the output felt templated.
//
// Two things are separated here, and the separation is the point:
//
//   - ABSOLUTE rules, which hold for every image this product publishes. No
//     text, no collage, no borders. These are not style; they are the contract
//     with the caption, and nothing may route around them.
//
//   - STYLE, which must differ by what the business is and what the post is
//     trying to do. This is the part that used to be one hardcoded sentence.
//
// Scope note: these images carry NO text — the words live in the caption. So
// the poster rules about headlines, contrast between type and background, and
// CTA placement have no home yet. What survives translation to photography is
// "one dominant idea", "people only when they earn their place", "a concept
// rather than an object on a background", and "legible on a phone", which is
// about the subject being readable at thumbnail size rather than about type.
// =============================================================================
/**
 * What the business is, as far as the camera is concerned.
 *
 * Grouped by how they should be SHOT, not by how a directory would file them.
 * A pharmacy and a dental clinic are one category here because both want
 * clean, calm, trustworthy framing; a boutique and a tailor are one because
 * both want the garment on a body in good light.
 */
export const INDUSTRY_CATEGORIES = [
    "retail",
    "food",
    "tech",
    "automotive",
    "fashion",
    "beauty",
    "health",
    "services",
    "property",
    "education",
    "general",
];
/**
 * Per-trade direction.
 *
 * Written as instructions to a photographer rather than adjectives, because
 * adjectives are satisfied by whatever the model was going to produce anyway.
 * "Appetising" means nothing to an image model; "close enough to see the
 * texture, steam if the dish is hot" changes the frame.
 */
export const ART_DIRECTION = {
    retail: {
        composition: "Real merchandise, generously arranged so the range is visible. A believable shop " +
            "setting rather than a studio void. Stock looks full and fresh, shelves and counters " +
            "look like somewhere people actually buy things.",
        hook: "abundance, value, freshness, or one specific offer worth crossing the road for",
        peopleUsuallyHelp: false,
    },
    food: {
        composition: "Close enough to see texture. Natural directional light, steam or moisture where the " +
            "dish would really have it, ingredients looking fresh rather than styled into " +
            "plastic. Minimal props, nothing competing with the food.",
        hook: "appetite, freshness, indulgence, or how easily it can be had right now",
        peopleUsuallyHelp: false,
    },
    tech: {
        composition: "Clean, premium, uncrowded. Show the result the software produces or the work it " +
            "replaces, and prefer a simple real-world metaphor for an abstract feature. No " +
            "glowing circuitry, no floating holograms, no blue neon futurism.",
        hook: "a business problem solved, hours saved, or a visibly better way to work",
        peopleUsuallyHelp: true,
    },
    automotive: {
        composition: "Strong vehicle photography with a real road, yard or workshop behind it. Where the " +
            "service is about visibility or control, show that plainly: a vehicle and the means " +
            "of watching over it. Confident and reassuring, never alarming.",
        hook: "knowing where the vehicle is, staying in control, and the cost of not knowing",
        peopleUsuallyHelp: false,
    },
    fashion: {
        composition: "The garment worn, on a real body, in light that shows the cloth. Fit, drape and " +
            "finish readable. The setting should flatter the clothing without becoming the subject.",
        hook: "how it will look on them, the craft in the finish, or the occasion it is for",
        peopleUsuallyHelp: true,
    },
    beauty: {
        composition: "The result, close and well lit. Skin and hair look like skin and hair, not " +
            "retouched plastic. A clean, calm salon or treatment setting, never clinical.",
        hook: "the visible result, the care taken, or how someone will feel walking out",
        peopleUsuallyHelp: true,
    },
    health: {
        composition: "Calm, clean, trustworthy. Orderly surfaces and honest light. Nothing that implies a " +
            "diagnosis, a cure or a clinical outcome. No distress, no illness depicted.",
        hook: "reassurance, availability, and being looked after properly",
        peopleUsuallyHelp: true,
    },
    services: {
        composition: "The work being done competently, in the real setting it happens in. Equipment, " +
            "vehicles, hands at work, finished results. Specific beats generic: one real job " +
            "rather than an abstraction of the trade.",
        hook: "proof of competence, reliability, and the problem taken off their hands",
        peopleUsuallyHelp: true,
    },
    property: {
        composition: "Architecture and space, shot wide enough to read the layout, in good daylight. " +
            "Straight verticals. Show the condition and the light honestly — a room oversold in " +
            "a photograph is a wasted viewing.",
        hook: "the space itself, the location, or what it would be like to live or work there",
        peopleUsuallyHelp: false,
    },
    education: {
        composition: "Learning actually happening — focused faces, materials in use, a real room. Warm " +
            "and purposeful. Avoid staged rows of people smiling at a camera.",
        hook: "what they will be able to do afterwards, and who is teaching it",
        peopleUsuallyHelp: true,
    },
    general: {
        composition: "One clear subject in a believable setting, lit naturally, with room to breathe. " +
            "Specific and concrete rather than a generic stand-in for the trade.",
        hook: "one concrete thing worth stopping for",
        peopleUsuallyHelp: false,
    },
};
/**
 * Free-text industry -> a category.
 *
 * `Brand.industry` is a 120-character text box, so this has to cope with
 * "provision shop", "GPS vehicle tracking & fleet", "Chop bar" and "". It is
 * keyword matching, which is crude, and the crudeness is bounded: the worst
 * outcome is `general`, which is the direction the whole product used to apply
 * to everything.
 *
 * Longest match wins, so "food delivery app" resolves on "delivery app" rather
 * than "food", and "car wash" does not lose to "car". Ties break on the order
 * of INDUSTRY_CATEGORIES, which is why `retail` and `food` sit before the
 * trades that often borrow their words.
 */
const KEYWORDS = {
    retail: [
        "provision",
        "supermarket",
        "mini mart",
        "minimart",
        "grocer",
        "groceries",
        "shop",
        "store",
        "retail",
        "wholesale",
        "market",
        "trading",
        "hardware",
        "stationery",
    ],
    food: [
        "restaurant",
        "chop bar",
        "chopbar",
        "food",
        "catering",
        "caterer",
        "bakery",
        "baker",
        "pastry",
        "cafe",
        "coffee",
        "kitchen",
        "eatery",
        "takeaway",
        "fast food",
        "juice",
        "smoothie",
        "snack",
        "pizza",
        "grill",
    ],
    tech: [
        "software",
        "saas",
        "app",
        "delivery app",
        "tech",
        "technology",
        "it services",
        "it support",
        "web",
        "website",
        "developer",
        "data",
        "cloud",
        "fintech",
        "payment",
        "pos",
        "platform",
        "startup",
    ],
    automotive: [
        "car",
        "cars",
        "vehicle",
        "automotive",
        "auto",
        "gps",
        "tracking",
        "tracker",
        "fleet",
        "mechanic",
        "garage",
        "spare parts",
        "tyre",
        "tire",
        "car hire",
        "car rental",
        "uber",
        "taxi",
        "transport",
        "haulage",
        "truck",
    ],
    fashion: [
        "fashion",
        "tailor",
        "tailoring",
        "seamstress",
        "boutique",
        "clothing",
        "clothes",
        "apparel",
        "garment",
        "shoe",
        "shoes",
        "footwear",
        "bag",
        "jewellery",
        "jewelry",
        "accessories",
        "fabric",
        "textile",
        "kente",
    ],
    beauty: [
        "salon",
        "saloon",
        "barber",
        "hair",
        "beauty",
        "cosmetic",
        "cosmetics",
        "make up",
        "makeup",
        "nail",
        "spa",
        "skincare",
        "skin care",
        "wig",
        "braid",
    ],
    health: [
        "pharmacy",
        "chemist",
        "clinic",
        "hospital",
        "dental",
        "dentist",
        "optical",
        "optician",
        "health",
        "medical",
        "herbal",
        "laboratory",
        "diagnostic",
        "physio",
        "wellness",
    ],
    services: [
        "service",
        "services",
        "consult",
        "consulting",
        "consultancy",
        "law",
        "lawyer",
        "legal",
        "account",
        "accounting",
        "audit",
        "logistics",
        "courier",
        "shipping",
        "cleaning",
        "laundry",
        "event",
        "events",
        "photography",
        "printing",
        "security",
        "insurance",
        "agency",
        "repair",
        "plumb",
        "electric",
        "welding",
        "carpent",
    ],
    property: [
        "real estate",
        "property",
        "properties",
        "estate",
        "rental",
        "rentals",
        "land",
        "construction",
        "builder",
        "building",
        "architect",
        "hostel",
        "apartment",
        "hotel",
        "guest house",
        "lodge",
    ],
    education: [
        "school",
        "academy",
        "education",
        "training",
        "tutor",
        "tutoring",
        "lesson",
        "college",
        "university",
        "institute",
        "course",
        "courses",
        "learning",
        "creche",
        "daycare",
    ],
};
export function resolveIndustryCategory(industry) {
    const text = (industry ?? "").toLowerCase().trim();
    if (!text)
        return "general";
    let best = null;
    for (const category of INDUSTRY_CATEGORIES) {
        if (category === "general")
            continue;
        for (const word of KEYWORDS[category]) {
            if (!text.includes(word))
                continue;
            // Strictly longer, so an earlier category keeps a tie. That ordering is
            // declared, not incidental: see the note above KEYWORDS.
            if (!best || word.length > best.length) {
                best = { category, length: word.length };
            }
        }
    }
    return best?.category ?? "general";
}
// ─────────────────────────────────────────────────────────────────────────────
// Creative strategy: what kind of picture this particular post wants.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The directions a post can take.
 *
 * `typography_led` is deliberately absent. It means "make the headline itself
 * the visual", and these images contain no words at all — offering it would be
 * offering a strategy the renderer cannot execute, and the model would pick it
 * and then produce an ordinary product shot. It belongs with a composition
 * engine that can set type, and not before.
 */
export const CREATIVE_STRATEGIES = [
    "product_led",
    "human_story_led",
    "concept_led",
    "transformation_led",
];
export const STRATEGY_DIRECTION = {
    product_led: {
        summary: "the product or the work itself is the hero, shown at its best",
        imageDirection: "The product fills the frame and is unmistakably the subject. No person required, " +
            "and no person unless they genuinely add something.",
        needsPeople: false,
    },
    human_story_led: {
        summary: "a believable person experiencing the benefit",
        imageDirection: "One person, caught mid-moment rather than posed for a camera. Natural expression, " +
            "clothing and surroundings that fit where this business actually is. The benefit " +
            "should be legible from what they are doing.",
        needsPeople: true,
    },
    concept_led: {
        summary: "an unexpected but relevant visual idea that carries the benefit",
        imageDirection: "A visual metaphor, an unusual angle, or a simple demonstration of the benefit. It " +
            "must still be immediately readable — a clever picture nobody understands has " +
            "failed. Not the product sitting on a plain background.",
        needsPeople: false,
    },
    transformation_led: {
        summary: "the difference between the problem and the solution",
        imageDirection: "Show the after state clearly, with just enough of the before to make the change " +
            "obvious. A single frame, not a split-screen collage and not a before/after grid.",
        needsPeople: false,
    },
};
/**
 * What the model is told when choosing, including what not to repeat.
 *
 * The repetition guard matters more than the menu. Given the same brand and a
 * similar topic, the model picks the same strategy every time — which is how
 * a thirty-post month becomes thirty product shots even with four options
 * available. Recent choices are fed back the same way recent captions already
 * are.
 */
export function strategyMenu(recent) {
    const lines = [
        "## Pick the visual approach",
        "",
        "Choose the one that suits this post and return it as `creative_strategy`:",
        ...CREATIVE_STRATEGIES.map((s) => `- ${s}: ${STRATEGY_DIRECTION[s].summary}`),
        "",
        "Pick what fits the message, not whichever sounds most elaborate. A plain " +
            "product shot is the right answer when the product is the news.",
    ];
    const used = (recent ?? []).filter((s) => CREATIVE_STRATEGIES.includes(s));
    if (used.length) {
        const counts = new Map();
        for (const s of used)
            counts.set(s, (counts.get(s) ?? 0) + 1);
        const summary = [...counts.entries()].map(([s, n]) => `${s} (${n})`).join(", ");
        lines.push("", `Recently used for this brand: ${summary}.`, "Prefer an approach that is not in that list, unless this post plainly calls for one " +
            "that is. Ten posts that all look the same is the failure this is here to prevent.");
    }
    return lines;
}
// ─────────────────────────────────────────────────────────────────────────────
// The two halves of an image prompt.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * True of every image this product publishes, regardless of anything else.
 *
 * The text ban is stated several ways on purpose. Image models reliably slip
 * signage, price tags and watermarks into anything that resembles an advert,
 * and a picture that states its own offer will eventually contradict the
 * caption that states the real one. Until there is a typography layer that
 * owns exact strings, no text is the only safe answer.
 */
export const ABSOLUTE_IMAGE_RULES = "Absolutely no text anywhere in the image: no words, letters, numbers, labels, signs, " +
    "price tags, logos, watermarks, captions or packaging copy. No collage, no split " +
    "screens, no borders, no frames, no added graphics or badges.";
/**
 * The styling half, appended at the point of the image call.
 *
 * Replaces the single hardcoded "clean and simple product photography"
 * sentence that used to be appended to everything. Order is deliberate:
 * strategy first because it decides the kind of picture, then the trade's
 * framing, then the brand's own look, then the absolute rules last so they are
 * the final thing read.
 */
export function buildImageStyle(input) {
    const art = ART_DIRECTION[input.category] ?? ART_DIRECTION.general;
    const parts = [];
    if (input.strategy) {
        parts.push(STRATEGY_DIRECTION[input.strategy].imageDirection);
    }
    parts.push(art.composition);
    // One dominant idea, and readable small. Both survive the move from posters
    // to photographs: a crowded frame is unreadable in a feed at thumbnail size,
    // which is where nearly everyone sees it.
    parts.push("One dominant subject, clearly separated from its background. It must still read at " +
        "thumbnail size on a phone, so nothing crowded, busy or fussy.");
    if (input.photographyStyle?.trim()) {
        parts.push(`House photographic style: ${input.photographyStyle.trim()}.`);
    }
    const palette = [input.primaryColor, ...(input.supportingColors ?? [])]
        .map((c) => c?.trim())
        .filter((c) => Boolean(c));
    if (palette.length) {
        // Props and setting, not a colour wash. Asked to "use" a palette, image
        // models tint the whole frame, and a brand-coloured photograph of food
        // stops looking like food.
        parts.push(`Let the brand palette show through the setting, props and surfaces — ${palette.join(", ")} — as accents a photographer would choose, not as a colour filter over the image.`);
    }
    parts.push(ABSOLUTE_IMAGE_RULES);
    return parts.join(" ");
}
/** Narrows a value off the wire to a strategy, or null. */
export function asCreativeStrategy(value) {
    return typeof value === "string" &&
        CREATIVE_STRATEGIES.includes(value)
        ? value
        : null;
}
//# sourceMappingURL=artDirection.js.map